/**
 * Dashboard web session: opaque BFF cookie via same-origin `/api/auth/*`.
 * No access/refresh/ID tokens in the browser. Admin role is enforced on the
 * Worker from D1 (UI guards are not authoritative).
 */

export type BffSessionProbe = {
  authenticated: true;
  accountId: string;
  clientId: string;
  scope: string | null;
  expiresAt: number;
};

export type BffProfile = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName?: string | null;
  username?: string | null;
  role: string;
  accountType?: string;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  onboardingCompleted?: boolean;
  subscriptionId?: string | null;
};

export const fetchBffSession = async (): Promise<BffSessionProbe | null> => {
  try {
    const res = await fetch("/api/auth/session", {
      method: "GET",
      credentials: "same-origin",
      headers: { accept: "application/json" },
      cache: "no-store",
    });
    if (!res.ok) return null;
    const json = (await res.json()) as {
      authenticated?: boolean;
      accountId?: string;
      clientId?: string;
      scope?: string | null;
      expiresAt?: number;
    };
    if (!json.authenticated || !json.accountId || !json.clientId) return null;
    return {
      authenticated: true,
      accountId: json.accountId,
      clientId: json.clientId,
      scope: json.scope ?? null,
      expiresAt: Number(json.expiresAt) || 0,
    };
  } catch {
    return null;
  }
};

export const fetchBffCurrentUser = async (): Promise<BffProfile | null> => {
  try {
    const res = await fetch("/api/resource/me", {
      method: "GET",
      credentials: "same-origin",
      headers: { accept: "application/json" },
      cache: "no-store",
    });
    if (!res.ok) return null;
    const json = (await res.json()) as {
      authenticated?: boolean;
      success?: boolean;
      data?: BffProfile;
    };
    if (!json.authenticated || !json.data?.id) return null;
    return json.data;
  } catch {
    return null;
  }
};

export const logoutBffSession = async (opts?: {
  global?: boolean;
}): Promise<boolean> => {
  const qs = opts?.global ? "?scope=global" : "";
  try {
    const res = await fetch(`/api/auth/logout${qs}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { accept: "application/json" },
    });
    return res.ok;
  } catch {
    return false;
  }
};

export const startBffLogin = (returnTo: string): void => {
  const safe =
    returnTo.startsWith("/") && !returnTo.startsWith("//") ? returnTo : "/overview";
  // Explicit client=dashboard so login works even when Origin is omitted.
  window.location.assign(
    `/api/auth/login?client=dashboard&returnTo=${encodeURIComponent(safe)}`,
  );
};